import { DEFAULT_DRILL_RULES, DRILL_FREQUENCIES, type DrillFrequencyKind } from '@felix/domain';
import {
  Alert,
  Button,
  Drawer,
  Group,
  Loader,
  NumberInput,
  Select,
  Switch,
  Textarea,
  TextInput,
} from '@mantine/core';
import { IconEdit } from '@tabler/icons-react';
import { type ReactNode, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { StatusBadge } from '../../StatusBadge.js';
import { errorCode } from '../personnel/source.js';
import classes from './Drills.module.css';
import { frequencyLabel } from './labels.js';
import type { DrillLibrarySource } from './source.js';
import type { DrillCategory, DrillLibrary, DrillTemplateDetail } from './types.js';
import { formOfTemplate, issuesOf, type TemplateForm, templateOfForm } from './view.js';

/** Tatbikat kütüphanesi hata metni: bilinen kod (alanlarıyla) ya da genel ileti. */
export function useDrillErrorText() {
  const { t } = useTranslation();
  return (error: unknown) => {
    const code = errorCode(error);
    const detail = (error as { detail?: unknown } | null)?.detail;
    const text = typeof detail === 'string' ? detail : '';
    if (code === 'INVALID_DRILL_TEMPLATE' || code === 'INVALID_DRILL_CATEGORY') {
      const fields = issuesOf(text)
        .map((issue) => {
          const field = issue.path.replace(/^content\./, '').split('.')[0] ?? issue.path;
          return `${t(`drills.fields.${field}`, { defaultValue: field })} (${t(
            `drills.issues.${issue.code}`,
            { defaultValue: issue.code },
          )})`;
        })
        .join(', ');
      return t('drills.errors.INVALID_FIELDS', { fields: fields || text });
    }
    const known = t(`drills.errors.${code}`, { defaultValue: '', detail: text });
    return known || t('drills.errors.GENERIC', { code });
  };
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className={classes.section}>
      <h3>{title}</h3>
      {children}
    </section>
  );
}

function List({ items, ordered = false }: { items: readonly string[]; ordered?: boolean }) {
  if (items.length === 0) return <p>—</p>;
  const Tag = ordered ? 'ol' : 'ul';
  return (
    <Tag>
      {items.map((item, index) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: aynı metin iki kez geçebilir; sıra sabit
        <li key={index}>{item}</li>
      ))}
    </Tag>
  );
}

/** Şablonun ayrıntısı ve sürümleri (belge §4). */
export function TemplateDetailDrawer({
  id,
  library,
  source,
  canEdit,
  onClose,
  onEdit,
}: {
  id: string | null;
  library: DrillLibrary;
  source: DrillLibrarySource;
  canEdit: boolean;
  onClose: () => void;
  onEdit: (detail: DrillTemplateDetail) => void;
}) {
  const { t, i18n } = useTranslation();
  const errorText = useDrillErrorText();
  const [detail, setDetail] = useState<DrillTemplateDetail | null>(null);
  const [failure, setFailure] = useState<{ error: unknown } | null>(null);
  useEffect(() => {
    setDetail(null);
    setFailure(null);
    if (id === null) return;
    let live = true;
    source.template(id).then(
      (value) => {
        if (live) setDetail(value);
      },
      (error: unknown) => {
        if (live) setFailure({ error });
      },
    );
    return () => {
      live = false;
    };
  }, [id, source]);
  const error = failure ? errorText(failure.error) : null;
  const names = new Map(library.categories.map((c) => [c.id, c.name]));
  const f = (key: string) => t(`drills.fields.${key}`);
  const date = (iso: string) =>
    new Intl.DateTimeFormat(i18n.language, { dateStyle: 'medium', timeZone: 'UTC' }).format(
      new Date(iso),
    );
  const c = detail?.content;
  const rule = c?.ruleCode ? DEFAULT_DRILL_RULES.find((r) => r.code === c.ruleCode) : undefined;
  return (
    <Drawer
      opened={id !== null}
      onClose={onClose}
      closeButtonProps={{ 'aria-label': t('drills.close') }}
      position="right"
      size="xl"
      title={detail ? `${detail.code} · ${detail.name}` : t('drills.detail.title')}
    >
      {error ? <Alert color="red">{error}</Alert> : null}
      {!detail || !c ? (
        error ? null : (
          <Loader size="sm" aria-label={t('drills.detail.loading')} />
        )
      ) : (
        <div className={classes.detail}>
          <Group justify="space-between">
            <Group gap="xs">
              <StatusBadge tone={detail.active ? 'success' : 'neutral'}>
                {t(detail.active ? 'drills.status.active' : 'drills.status.inactive')}
              </StatusBadge>
              <StatusBadge tone="info">v{detail.version}</StatusBadge>
            </Group>
            {canEdit ? (
              <Button
                variant="default"
                leftSection={<IconEdit size={16} aria-hidden />}
                onClick={() => onEdit(detail)}
              >
                {t('drills.actions.edit')}
              </Button>
            ) : null}
          </Group>
          <dl className={classes.facts}>
            <div>
              <dt>{f('category')}</dt>
              <dd>{names.get(detail.categoryId) ?? '—'}</dd>
            </div>
            <div>
              <dt>{f('subcategory')}</dt>
              <dd>{detail.subcategoryId ? (names.get(detail.subcategoryId) ?? '—') : '—'}</dd>
            </div>
            <div>
              <dt>{f('frequency')}</dt>
              <dd>{frequencyLabel(t, c.frequency)}</dd>
            </div>
            <div>
              <dt>{f('ruleCode')}</dt>
              <dd>{rule ? `${rule.code} · ${rule.reference}` : '—'}</dd>
            </div>
            <div>
              <dt>{f('regulatoryReference')}</dt>
              <dd>{c.regulatoryReference ?? '—'}</dd>
            </div>
            <div>
              <dt>{f('smsProcedure')}</dt>
              <dd>{c.smsProcedure ?? '—'}</dd>
            </div>
            <div>
              <dt>{f('vesselTypes')}</dt>
              <dd>
                {c.vesselTypes.length ? c.vesselTypes.join(', ') : t('drills.detail.allVessels')}
              </dd>
            </div>
            <div>
              <dt>{f('source')}</dt>
              <dd>{t(`drills.sources.${detail.source}`, { defaultValue: detail.source })}</dd>
            </div>
          </dl>
          {rule ? <p className={classes.hint}>{rule.note}</p> : null}
          <Section title={f('objective')}>
            <p>{c.objective}</p>
          </Section>
          <Section title={f('scenario')}>
            <p>{c.scenario}</p>
          </Section>
          <Section title={f('steps')}>
            <List items={c.steps} ordered />
          </Section>
          <Section title={f('equipment')}>
            <List items={c.equipment} />
          </Section>
          <Section title={f('roles')}>
            <List items={c.roles.map((r) => (r.duty ? `${r.role}: ${r.duty}` : r.role))} />
          </Section>
          <Section title={f('preparation')}>
            <List items={c.preparation} />
          </Section>
          <Section title={f('safety')}>
            <List items={c.safety} />
          </Section>
          <Section title={f('criteria')}>
            <List
              items={c.criteria.map((k) => `${k.label} — ${t(`drills.criterionKinds.${k.kind}`)}`)}
            />
          </Section>
          <Section title={f('debriefing')}>
            <List items={c.debriefing} />
          </Section>
          <Section title={f('correctiveActions')}>
            <List items={c.correctiveActions} />
          </Section>
          {c.reportTemplate ? (
            <Section title={f('reportTemplate')}>
              <p>{c.reportTemplate}</p>
            </Section>
          ) : null}
          <Section title={t('drills.detail.versions')}>
            <table className={classes.versions}>
              <thead>
                <tr>
                  <th scope="col">{t('drills.columns.version')}</th>
                  <th scope="col">{t('drills.columns.name')}</th>
                  <th scope="col">{t('drills.columns.date')}</th>
                  <th scope="col">{t('drills.columns.by')}</th>
                </tr>
              </thead>
              <tbody>
                {detail.versions.map((v) => (
                  <tr key={v.id}>
                    <td>
                      v{v.version}
                      {v.active ? '' : ` · ${t('drills.status.inactive')}`}
                    </td>
                    <td>{v.name}</td>
                    <td>{date(v.createdAt)}</td>
                    <td>{v.createdByName ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className={classes.hint}>{t('drills.detail.versionNote')}</p>
          </Section>
        </div>
      )}
    </Drawer>
  );
}

const LIST_FIELDS = [
  'steps',
  'equipment',
  'roles',
  'preparation',
  'safety',
  'criteria',
  'debriefing',
  'correctiveActions',
] as const;

/** Şablon ekleme ve düzeltme (ofis): içerik değişince yeni değiştirilemez sürüm (ADR 0128 §3). */
export function TemplateEditDrawer({
  target,
  library,
  source,
  onClose,
  onSaved,
}: {
  target: { detail: DrillTemplateDetail | null; categoryId: string | null } | null;
  library: DrillLibrary;
  source: DrillLibrarySource;
  onClose: () => void;
  onSaved: (saved: DrillTemplateDetail) => Promise<void>;
}) {
  const { t } = useTranslation();
  const errorText = useDrillErrorText();
  const [form, setForm] = useState<TemplateForm>(() => formOfTemplate(null));
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (target === null) return;
    const top = library.categories.find((c) => c.id === target.categoryId);
    const parent = top?.parentId ?? target.categoryId;
    const initial = formOfTemplate(target.detail, parent);
    if (!target.detail && top?.parentId) initial.subcategoryId = top.id;
    setForm(initial);
    setError(null);
  }, [target, library.categories]);
  const set = <K extends keyof TemplateForm>(key: K, value: TemplateForm[K]) =>
    setForm((current) => ({ ...current, [key]: value }));
  const f = (key: string) => t(`drills.fields.${key}`);
  const editing = target?.detail ?? null;
  const tops = library.categories.filter(
    (c) => c.parentId === null && (c.active || c.id === form.categoryId),
  );
  const subs = library.categories.filter(
    (c) =>
      c.parentId !== null &&
      c.parentId === form.categoryId &&
      (c.active || c.id === form.subcategoryId),
  );
  const save = async () => {
    if (!source.saveTemplate) return;
    setBusy(true);
    setError(null);
    try {
      const saved = await source.saveTemplate({
        ...(editing ? { id: editing.id, rowVersion: editing.rowVersion } : {}),
        template: templateOfForm(form),
      });
      await onSaved(saved);
    } catch (failure) {
      setError(errorText(failure));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Drawer
      opened={target !== null}
      onClose={onClose}
      closeButtonProps={{ 'aria-label': t('drills.close') }}
      position="right"
      size="xl"
      title={
        editing ? t('drills.edit.titleEdit', { code: editing.code }) : t('drills.edit.titleNew')
      }
    >
      <form
        onSubmit={(event) => {
          event.preventDefault();
          void save();
        }}
      >
        <div className={classes.formGrid}>
          {editing ? (
            <p className={`${classes.hint} ${classes.full}`}>{t('drills.edit.versionHint')}</p>
          ) : null}
          <TextInput
            label={f('code')}
            required
            value={form.code}
            disabled={editing !== null}
            description={editing ? t('drills.edit.codeFixed') : undefined}
            onChange={(event) => set('code', event.currentTarget.value)}
          />
          <TextInput
            label={f('name')}
            required
            value={form.name}
            onChange={(event) => set('name', event.currentTarget.value)}
          />
          <Select
            label={f('category')}
            required
            data={tops.map((c) => ({ value: c.id, label: c.name }))}
            value={form.categoryId}
            onChange={(value) =>
              setForm((cur) => ({ ...cur, categoryId: value, subcategoryId: null }))
            }
          />
          <Select
            label={f('subcategory')}
            clearable
            data={subs.map((c) => ({ value: c.id, label: c.name }))}
            value={form.subcategoryId}
            onChange={(value) => set('subcategoryId', value)}
          />
          <Select
            label={f('frequency')}
            required
            allowDeselect={false}
            data={DRILL_FREQUENCIES.map((kind) => ({
              value: kind,
              label:
                kind === 'custom'
                  ? t('drills.frequency.customOption')
                  : t(`drills.frequency.${kind}`),
            }))}
            value={form.frequencyKind}
            onChange={(value) =>
              set('frequencyKind', (value as DrillFrequencyKind | null) ?? 'monthly')
            }
          />
          <Select
            label={f('ruleCode')}
            clearable
            data={DEFAULT_DRILL_RULES.map((r) => ({
              value: r.code,
              label: `${r.code} · ${r.reference}`,
            }))}
            value={form.ruleCode}
            onChange={(value) => set('ruleCode', value)}
          />
          {form.frequencyKind === 'custom' ? (
            <NumberInput
              label={f('months')}
              required
              min={1}
              max={60}
              allowDecimal={false}
              value={form.frequencyMonths ?? ''}
              onChange={(value) => set('frequencyMonths', typeof value === 'number' ? value : null)}
            />
          ) : null}
          <TextInput
            label={f('regulatoryReference')}
            value={form.regulatoryReference}
            onChange={(event) => set('regulatoryReference', event.currentTarget.value)}
          />
          <TextInput
            label={f('smsProcedure')}
            value={form.smsProcedure}
            onChange={(event) => set('smsProcedure', event.currentTarget.value)}
          />
          <Textarea
            className={classes.full}
            label={f('vesselTypes')}
            description={t('drills.edit.vesselTypesHint')}
            autosize
            minRows={1}
            value={form.vesselTypes}
            onChange={(event) => set('vesselTypes', event.currentTarget.value)}
          />
          <Textarea
            className={classes.full}
            label={f('objective')}
            required
            autosize
            minRows={2}
            value={form.objective}
            onChange={(event) => set('objective', event.currentTarget.value)}
          />
          <Textarea
            className={classes.full}
            label={f('scenario')}
            required
            autosize
            minRows={3}
            value={form.scenario}
            onChange={(event) => set('scenario', event.currentTarget.value)}
          />
          {LIST_FIELDS.map((key) => (
            <Textarea
              key={key}
              className={classes.full}
              label={f(key)}
              required={key === 'steps'}
              description={t(`drills.edit.hints.${key}`)}
              autosize
              minRows={key === 'steps' ? 5 : 2}
              value={form[key]}
              onChange={(event) => set(key, event.currentTarget.value)}
            />
          ))}
          <Textarea
            className={classes.full}
            label={f('reportTemplate')}
            autosize
            minRows={2}
            value={form.reportTemplate}
            onChange={(event) => set('reportTemplate', event.currentTarget.value)}
          />
          <Switch
            className={classes.full}
            label={f('active')}
            checked={form.active}
            onChange={(event) => set('active', event.currentTarget.checked)}
          />
          {error ? (
            <Alert color="red" className={classes.full}>
              {error}
            </Alert>
          ) : null}
        </div>
        <Group justify="flex-end" mt="md">
          <Button variant="default" onClick={onClose}>
            {t('drills.actions.cancel')}
          </Button>
          <Button type="submit" loading={busy} disabled={form.categoryId === null}>
            {t('drills.actions.save')}
          </Button>
        </Group>
      </form>
    </Drawer>
  );
}

/** Kategori ekleme ve düzeltme (ofis): iki düzey; kod değişmez, kaldırma yerine pasif yapma. */
export function CategoryDrawer({
  target,
  library,
  source,
  onClose,
  onSaved,
}: {
  target: { category: DrillCategory | null } | null;
  library: DrillLibrary;
  source: DrillLibrarySource;
  onClose: () => void;
  onSaved: () => Promise<void>;
}) {
  const { t } = useTranslation();
  const errorText = useDrillErrorText();
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [parentId, setParentId] = useState<string | null>(null);
  const [sortOrder, setSortOrder] = useState(0);
  const [active, setActive] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const editing = target?.category ?? null;
  useEffect(() => {
    if (target === null) return;
    const c = target.category;
    setCode(c?.code ?? '');
    setName(c?.name ?? '');
    setParentId(c?.parentId ?? null);
    setSortOrder(c?.sortOrder ?? library.categories.filter((x) => x.parentId === null).length + 1);
    setActive(c?.active ?? true);
    setError(null);
  }, [target, library.categories]);
  const hasChildren = editing ? library.categories.some((c) => c.parentId === editing.id) : false;
  const parents = library.categories.filter(
    (c) => c.parentId === null && c.id !== editing?.id && (c.active || c.id === parentId),
  );
  const f = (key: string) => t(`drills.fields.${key}`);
  const save = async () => {
    if (!source.saveCategory) return;
    setBusy(true);
    setError(null);
    try {
      await source.saveCategory({
        ...(editing ? { id: editing.id, rowVersion: editing.rowVersion } : {}),
        category: { code: code.trim().toUpperCase(), name, parentId, sortOrder, active },
      });
      await onSaved();
    } catch (failure) {
      setError(errorText(failure));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Drawer
      opened={target !== null}
      onClose={onClose}
      closeButtonProps={{ 'aria-label': t('drills.close') }}
      position="right"
      size="md"
      title={
        editing
          ? t('drills.category.titleEdit', { code: editing.code })
          : t('drills.category.titleNew')
      }
    >
      <form
        onSubmit={(event) => {
          event.preventDefault();
          void save();
        }}
      >
        <div className={classes.formGrid}>
          <TextInput
            className={classes.full}
            label={f('code')}
            required
            value={code}
            disabled={editing !== null}
            description={editing ? t('drills.edit.codeFixed') : undefined}
            onChange={(event) => setCode(event.currentTarget.value)}
          />
          <TextInput
            className={classes.full}
            label={f('categoryName')}
            required
            value={name}
            onChange={(event) => setName(event.currentTarget.value)}
          />
          <Select
            className={classes.full}
            label={f('parent')}
            clearable
            disabled={hasChildren}
            description={
              hasChildren ? t('drills.category.hasChildren') : t('drills.category.parentHint')
            }
            data={parents.map((c) => ({ value: c.id, label: c.name }))}
            value={parentId}
            onChange={setParentId}
          />
          <NumberInput
            label={f('sortOrder')}
            min={0}
            max={9999}
            allowDecimal={false}
            value={sortOrder}
            onChange={(value) => setSortOrder(typeof value === 'number' ? value : 0)}
          />
          <Switch
            className={classes.full}
            label={f('active')}
            checked={active}
            onChange={(event) => setActive(event.currentTarget.checked)}
          />
          {error ? (
            <Alert color="red" className={classes.full}>
              {error}
            </Alert>
          ) : null}
        </div>
        <Group justify="flex-end" mt="md">
          <Button variant="default" onClick={onClose}>
            {t('drills.actions.cancel')}
          </Button>
          <Button type="submit" loading={busy}>
            {t('drills.actions.save')}
          </Button>
        </Group>
      </form>
    </Drawer>
  );
}
